<template>
  <div v-if="currentFacilityId">
    <router-view />
  </div>
  <v-progress-linear
    v-else-if="resolving"
    indeterminate
    color="primary"
    :height="3"
  />
  <LayoutPage v-else title="Page not found" />
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import useFacility from "../authentication/useFacility";
import useUser from "../authentication/useUser";
import { ref, watch, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router/composables";

export default {
  name: "FacilityWrapper",
  components: { LayoutPage },
  setup() {
    const { currentFacilityId } = useFacility();
    const { defaultPage } = useUser();
    const route = useRoute();
    const router = useRouter();

    // On a hard refresh of a facility-scoped URL the facility context is seeded
    // asynchronously (App.beforeMount -> selectFacility -> GET_FACILITY), so
    // currentFacilityId is briefly null. Show a loader meanwhile instead of
    // flashing "Page not found". If it never resolves — e.g. the route's
    // organization_id is an organization, not a facility, so setCurrentFacility
    // rejects it — redirect to the user's default landing rather than stranding
    // them on a dead end.
    const resolving = ref(!currentFacilityId.value);
    let timer = null;

    if (resolving.value) {
      const stop = watch(currentFacilityId, (id) => {
        if (id) {
          resolving.value = false;
          clearTimeout(timer);
          stop();
        }
      });
      timer = setTimeout(() => {
        stop();
        resolving.value = false;
        if (!currentFacilityId.value) {
          const target = defaultPage.value;
          // Guard against a redirect loop: never bounce back to the same route
          // (e.g. a single-facility user whose own facility genuinely failed to
          // load — better to show "Page not found" than spin forever).
          if (target?.name && target.name !== route.name) {
            router.replace(target).catch(() => {});
          }
        }
      }, 3000);
    }

    onBeforeUnmount(() => clearTimeout(timer));

    return { currentFacilityId, resolving };
  },
};
</script>
